import React from 'react';
import {
  Card,
  Button,
  InputGroup,
  MenuItem,
  Tag,
  Icon,
  Spinner,
  NonIdealState,
  Colors,
  Divider,
  H3,
  H4,
  H5,
  Position,
  ButtonGroup,
  Dialog,
  Classes,
  Intent
} from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { inject, observer } from 'mobx-react';
import cogoToast from 'cogo-toast';
import AuditService from '../services/AuditService';
import _ from 'lodash';

class AuditLogViewer extends React.Component {
  state = {
    isLoading: false,
    logs: [],
    total: 0,
    hasMore: false,
    currentPage: 0,
    pageSize: 20,
    filters: {
      entityType: '',
      entityId: '',
      action: '',
      userId: '',
      dateRange: {
        from: '',
        to: ''
      }
    },
    selectedLog: null,
    showLogDetail: false,
    searchText: ''
  };

  componentDidMount() {
    this.loadAuditLogs();
  }

  loadAuditLogs = async (reset = false) => {
    console.log('🔍 AUDIT LOG VIEWER: Starting to load audit logs', {
      reset,
      authStore: this.props.authStore ? 'present' : 'missing',
      prefix: this.props.authStore?.prefix
    });
    
    try {
      this.setState({ isLoading: true });
      
      const { filters, currentPage, pageSize, searchText } = this.state;
      const offset = reset ? 0 : currentPage * pageSize;

      // Build query parameters
      const queryParams = {
        limit: pageSize,
        offset: offset,
        ...filters
      };

      // Add search functionality
      if (searchText.trim()) {
        queryParams.searchText = searchText.trim();
      }

      console.log('🔍 AUDIT LOG VIEWER: Query parameters', {
        prefix: this.props.authStore.prefix,
        entityType: filters.entityType,
        entityId: filters.entityId,
        queryParams
      });

      const response = await AuditService.getAuditLogs(
        this.props.authStore.prefix,
        filters.entityType,
        filters.entityId,
        queryParams
      );

      console.log('🔍 AUDIT LOG VIEWER: API response', response);

      if (reset) {
        this.setState({
          logs: response.logs || [],
          total: response.total || 0,
          hasMore: response.hasMore || false,
          currentPage: 0
        });
      } else {
        this.setState(prevState => ({
          logs: [...prevState.logs, ...(response.logs || [])],
          total: response.total || 0,
          hasMore: response.hasMore || false,
          currentPage: prevState.currentPage + 1
        }));
      }
      
      console.log('✅ AUDIT LOG VIEWER: Successfully loaded logs', {
        logsCount: response.logs?.length || 0,
        total: response.total || 0
      });
    } catch (error) {
      console.error('❌ AUDIT LOG VIEWER: Error loading audit logs:', error);
      console.error('❌ AUDIT LOG VIEWER: Error details:', {
        message: error.message,
        stack: error.stack
      });
      cogoToast.error('Failed to load audit logs');
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleFilterChange = (filterKey, value) => {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        [filterKey]: value
      }
    }), () => {
      this.loadAuditLogs(true);
    });
  };

  handleDateRangeChange = (dateKey, value) => {
    this.setState(prevState => ({
      filters: {
        ...prevState.filters,
        dateRange: {
          ...prevState.filters.dateRange,
          [dateKey]: value
        }
      }
    }));
  };

  handleSearch = () => {
    this.loadAuditLogs(true);
  };

  handleClearFilters = () => {
    this.setState({
      filters: {
        entityType: '',
        entityId: '',
        action: '',
        userId: '',
        dateRange: {
          from: '',
          to: ''
        }
      },
      searchText: ''
    }, () => {
      this.loadAuditLogs(true);
    });
  };

  handleLoadMore = () => {
    this.loadAuditLogs(false);
  };

  handleViewLogDetail = (log) => {
    this.setState({
      selectedLog: log,
      showLogDetail: true
    });
  };

  handleCloseLogDetail = () => {
    this.setState({
      selectedLog: null,
      showLogDetail: false
    });
  };

  getActionIcon = (action) => {
    switch (action) {
      case 'CREATE':
        return <Icon icon="plus" intent={Intent.SUCCESS} />;
      case 'UPDATE':
        return <Icon icon="edit" intent={Intent.WARNING} />;
      case 'DELETE':
        return <Icon icon="trash" intent={Intent.DANGER} />;
      case 'VIEW':
        return <Icon icon="eye-open" intent={Intent.PRIMARY} />;
      default:
        return <Icon icon="help" />;
    }
  };

  getActionColor = (action) => {
    switch (action) {
      case 'CREATE':
        return Colors.GREEN3;
      case 'UPDATE':
        return Colors.ORANGE3;
      case 'DELETE':
        return Colors.RED3;
      case 'VIEW':
        return Colors.BLUE3;
      default:
        return Colors.GRAY3;
    }
  };

  formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  renderFilters = () => {
    const { filters, searchText } = this.state;

    return (
      <Card style={{ marginBottom: '16px', background: Colors.DARK_GRAY2 }}>
        <H4 style={{ color: Colors.LIGHT_GRAY1, marginBottom: '16px' }}>
          <Icon icon="filter" style={{ marginRight: '8px' }} />
          Filters & Search
        </H4>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label style={{ color: Colors.LIGHT_GRAY3, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              Entity Type
            </label>
            <Select
              items={['', 'insurancePanel', 'branch', 'patient', 'user']}
              itemRenderer={(item) => (
                <MenuItem key={item} text={item || 'All Types'} />
              )}
              onItemSelect={(item) => this.handleFilterChange('entityType', item)}
              filterable={false}
            >
              <Button
                text={filters.entityType || 'All Types'}
                rightIcon="caret-down"
                style={{ width: '100%', justifyContent: 'space-between' }}
              />
            </Select>
          </div>

          <div>
            <label style={{ color: Colors.LIGHT_GRAY3, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              Action
            </label>
            <Select
              items={['', 'CREATE', 'UPDATE', 'DELETE', 'VIEW']}
              itemRenderer={(item) => (
                <MenuItem key={item} text={item || 'All Actions'} />
              )}
              onItemSelect={(item) => this.handleFilterChange('action', item)}
              filterable={false}
            >
              <Button
                text={filters.action || 'All Actions'}
                rightIcon="caret-down"
                style={{ width: '100%', justifyContent: 'space-between' }}
              />
            </Select>
          </div>

          <div>
            <label style={{ color: Colors.LIGHT_GRAY3, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              Entity ID
            </label>
            <InputGroup
              placeholder="Enter entity ID"
              value={filters.entityId}
              onChange={(e) => this.handleFilterChange('entityId', e.target.value)}
              leftIcon="tag"
            />
          </div>

          <div>
            <label style={{ color: Colors.LIGHT_GRAY3, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              User ID
            </label>
            <InputGroup
              placeholder="Enter user ID"
              value={filters.userId}
              onChange={(e) => this.handleFilterChange('userId', e.target.value)}
              leftIcon="user"
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ color: Colors.LIGHT_GRAY3, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              From Date
            </label>
            <InputGroup
              type="datetime-local"
              value={filters.dateRange.from}
              onChange={(e) => this.handleDateRangeChange('from', e.target.value)}
              leftIcon="calendar"
            />
          </div>

          <div>
            <label style={{ color: Colors.LIGHT_GRAY3, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              To Date
            </label>
            <InputGroup
              type="datetime-local"
              value={filters.dateRange.to}
              onChange={(e) => this.handleDateRangeChange('to', e.target.value)}
              leftIcon="calendar"
            />
          </div>

          <ButtonGroup>
            <Button
              icon="search"
              intent={Intent.PRIMARY}
              onClick={this.handleSearch}
              disabled={this.state.isLoading}
            />
            <Button
              icon="refresh"
              onClick={this.handleClearFilters}
              disabled={this.state.isLoading}
            />
          </ButtonGroup>
        </div>

        <div style={{ marginTop: '12px' }}>
          <InputGroup
            placeholder="Search in descriptions..."
            value={searchText}
            onChange={(e) => this.setState({ searchText: e.target.value })}
            leftIcon="search"
            rightElement={
              <Button
                icon="arrow-right"
                minimal
                onClick={this.handleSearch}
                disabled={this.state.isLoading}
              />
            }
          />
        </div>
      </Card>
    );
  };

  renderLogItem = (log) => {
    return (
      <Card
        key={log._id}
        style={{
          marginBottom: '8px',
          background: Colors.DARK_GRAY2,
          border: `1px solid ${Colors.DARK_GRAY4}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        interactive
        onClick={() => this.handleViewLogDetail(log)}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {this.getActionIcon(log.action)}
              <Tag
                intent={log.action === 'CREATE' ? Intent.SUCCESS : 
                       log.action === 'UPDATE' ? Intent.WARNING :
                       log.action === 'DELETE' ? Intent.DANGER : Intent.PRIMARY}
                style={{ minWidth: '60px', textAlign: 'center' }}
              >
                {log.action}
              </Tag>
            </div>

            <div>
              <H5 style={{ color: Colors.LIGHT_GRAY1, margin: 0 }}>
                {log.entityType} - {log.entityId}
              </H5>
              <p style={{ color: Colors.LIGHT_GRAY3, margin: 0, fontSize: '12px' }}>
                {log.description}
              </p>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ color: Colors.LIGHT_GRAY2, fontSize: '12px', marginBottom: '4px' }}>
              {log.user.name}
            </div>
            <div style={{ color: Colors.LIGHT_GRAY4, fontSize: '11px' }}>
              {this.formatTimestamp(log.timestamp)}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  renderLogDetail = () => {
    const { selectedLog, showLogDetail } = this.state;

    if (!selectedLog) return null;

    return (
      <Dialog
        isOpen={showLogDetail}
        onClose={this.handleCloseLogDetail}
        title="Audit Log Details"
        style={{ background: Colors.DARK_GRAY2 }}
        className={Classes.DARK}
      >
        <div className={Classes.DIALOG_BODY}>
          <div style={{ marginBottom: '16px' }}>
            <H4 style={{ color: Colors.LIGHT_GRAY1, marginBottom: '8px' }}>
              {this.getActionIcon(selectedLog.action)} {selectedLog.action} - {selectedLog.entityType}
            </H4>
            <p style={{ color: Colors.LIGHT_GRAY3, margin: 0 }}>
              {selectedLog.description}
            </p>
          </div>

          <Divider />

          <div style={{ marginBottom: '16px' }}>
            <H5 style={{ color: Colors.LIGHT_GRAY1, marginBottom: '8px' }}>Entity Information</H5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <strong style={{ color: Colors.LIGHT_GRAY2 }}>Type:</strong>
                <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{selectedLog.entityType}</span>
              </div>
              <div>
                <strong style={{ color: Colors.LIGHT_GRAY2 }}>ID:</strong>
                <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{selectedLog.entityId}</span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <H5 style={{ color: Colors.LIGHT_GRAY1, marginBottom: '8px' }}>User Information</H5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <strong style={{ color: Colors.LIGHT_GRAY2 }}>Name:</strong>
                <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{selectedLog.user.name}</span>
              </div>
              <div>
                <strong style={{ color: Colors.LIGHT_GRAY2 }}>Email:</strong>
                <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{selectedLog.user.email}</span>
              </div>
              <div>
                <strong style={{ color: Colors.LIGHT_GRAY2 }}>User ID:</strong>
                <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{selectedLog.user.id}</span>
              </div>
              <div>
                <strong style={{ color: Colors.LIGHT_GRAY2 }}>Timestamp:</strong>
                <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{this.formatTimestamp(selectedLog.timestamp)}</span>
              </div>
            </div>
          </div>

          {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <H5 style={{ color: Colors.LIGHT_GRAY1, marginBottom: '8px' }}>Changes</H5>
              <div style={{ background: Colors.DARK_GRAY3, padding: '12px', borderRadius: '4px' }}>
                <pre style={{ color: Colors.LIGHT_GRAY3, fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(selectedLog.changes, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {selectedLog.technical && (
            <div>
              <H5 style={{ color: Colors.LIGHT_GRAY1, marginBottom: '8px' }}>Technical Information</H5>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <strong style={{ color: Colors.LIGHT_GRAY2 }}>IP Address:</strong>
                  <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{selectedLog.technical.ipAddress}</span>
                </div>
                <div>
                  <strong style={{ color: Colors.LIGHT_GRAY2 }}>Session ID:</strong>
                  <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{selectedLog.technical.sessionId}</span>
                </div>
                <div>
                  <strong style={{ color: Colors.LIGHT_GRAY2 }}>User Agent:</strong>
                  <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px', fontSize: '11px' }}>
                    {selectedLog.technical.userAgent?.substring(0, 50)}...
                  </span>
                </div>
                <div>
                  <strong style={{ color: Colors.LIGHT_GRAY2 }}>Timezone:</strong>
                  <span style={{ color: Colors.LIGHT_GRAY3, marginLeft: '8px' }}>{selectedLog.technical.timezone}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={this.handleCloseLogDetail}>Close</Button>
          </div>
        </div>
      </Dialog>
    );
  };

  render() {
    const { logs, isLoading, hasMore, total } = this.state;

    return (
      <div style={{ 
        padding: '24px',
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${Colors.DARK_GRAY5} 0%, ${Colors.DARK_GRAY4} 100%)`
      }}>
        <Card style={{ 
          background: Colors.DARK_GRAY2,
          border: `1px solid ${Colors.DARK_GRAY4}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                icon="arrow-left"
                minimal
                onClick={this.props.onClose}
                style={{ 
                  color: Colors.LIGHT_GRAY2,
                  borderRadius: '6px'
                }}
              />
              <H3 style={{ color: Colors.LIGHT_GRAY1, margin: 0 }}>
                <Icon icon="history" style={{ marginRight: '12px' }} />
                Audit Logs
              </H3>
            </div>
            <div style={{ color: Colors.LIGHT_GRAY3, fontSize: '14px' }}>
              {total} total logs
            </div>
          </div>

          {this.renderFilters()}

          <div style={{ minHeight: '400px' }}>
            {isLoading && logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spinner size={40} />
                <p style={{ color: Colors.LIGHT_GRAY3, marginTop: '16px' }}>Loading audit logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <NonIdealState
                icon="history"
                title="No audit logs found"
                description="No audit logs match your current filters. Try adjusting your search criteria."
              />
            ) : (
              <>
                {logs.map(log => this.renderLogItem(log))}
                
                {hasMore && (
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <Button
                      icon="more"
                      text="Load More"
                      onClick={this.handleLoadMore}
                      disabled={isLoading}
                      loading={isLoading}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </Card>

        {this.renderLogDetail()}
      </div>
    );
  }
}

export default inject('authStore')(observer(AuditLogViewer));
